import React, { useState } from "react";
import styled from "styled-components";
import { Coach as CoachModel } from "../model/Coach";
import { Division as DivisionModel, Week as WeekModel, PlayoffStructure } from "../model/Season";
import { Team as TeamModel } from "../model/Team";
import { createComponentLogger } from "../utils/logger";

const EditModeBanner = styled.div`
    background-color: #fff3cd;
    border: 1px solid #ffeaa7;
    color: #856404;
    padding: 0.5em;
    border-radius: 4px;
    margin-bottom: 0.75em;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CollapsibleSection = styled.div`
    margin-top: 1em;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
`;

const SectionHeader = styled.div<{ $isExpanded: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75em;
    background-color: #f8f9fa;
    border-bottom: ${props => props.$isExpanded ? '1px solid #ddd' : 'none'};
    cursor: pointer;
    
    &:hover {
        background-color: #e9ecef;
    }
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: #333;
`;

const ToggleButton = styled.button`
    background: none;
    border: none;
    font-size: 1.2em;
    cursor: pointer;
    color: #666;
    
    &:hover {
        color: #333;
    }
`;

const SectionContent = styled.div<{ $isExpanded: boolean }>`
    display: ${props => props.$isExpanded ? 'block' : 'none'};
    padding: 0.75em;
    background-color: #f8f9fa;
`;

const DivisionsList = styled.div`
    margin-top: 1em;
`;

const DivisionItem = styled.div`
    padding: 0.75em;
    margin: 0.25em 0;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f8f9fa;
`;

const DivisionHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5em;
`;

const DivisionName = styled.h3`
    margin: 0;
    color: #333;
`;

const DivisionActions = styled.div`
    display: flex;
    gap: 0.5em;
`;

const ActionButton = styled.button`
    padding: 0.25em 0.5em;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8em;
    
    &.edit {
        background-color: #28a745;
        color: white;
        
        &:hover {
            background-color: #218838;
        }
    }
    
    &.delete {
        background-color: #dc3545;
        color: white;
        
        &:hover {
            background-color: #c82333;
        }
    }
`;

const TeamsList = styled.div`
    margin-top: 0.5em;
`;

const TeamItem = styled.div`
    padding: 0.4em;
    margin: 0.15em 0;
    background-color: white;
    border-radius: 4px;
    border: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const CoachInfo = styled.div`
    flex: 1;
`;

const RemoveButton = styled.button`
    background-color: #dc3545;
    color: white;
    border: none;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8em;
    
    &:hover {
        background-color: #c82333;
    }
`;

const AddDivisionSection = styled.div`
    margin-top: 0.75em;
    padding: 0.75em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f8f9fa;
`;

const AddDivisionForm = styled.div`
    display: flex;
    gap: 0.5em;
    align-items: center;
`;

const Input = styled.input`
    padding: 0.5em;
    border: 1px solid #ccc;
    border-radius: 4px;
    flex: 1;
`;

const AddButton = styled.button`
    background-color: #28a745;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
        background-color: #218838;
    }
    
    &:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
`;

const AddCoachSection = styled.div`
    margin-top: 0.75em;
    padding: 0.75em;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #f8f9fa;
`;

const AddCoachForm = styled.div`
    display: flex;
    gap: 0.5em;
    align-items: center;
`;

const Select = styled.select`
    padding: 0.5em;
    border: 1px solid #ccc;
    border-radius: 4px;
    flex: 1;
`;

const WeeksList = styled.div`
    margin-top: 1em;
`;

const WeekItem = styled.div`
    padding: 0.75em;
    margin: 0.25em 0;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f8f9fa;
`;

const WeekHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5em;
`;

const WeekName = styled.h4`
    margin: 0;
    color: #333;
`;

const WeekActions = styled.div`
    display: flex;
    gap: 0.5em;
`;

const WeekTypeBadge = styled.span<{ type: 'preseason' | 'regular' | 'postseason' }>`
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: 500;
    margin-left: 0.5em;
    
    ${props => {
        switch (props.type) {
            case 'preseason':
                return 'background-color: #ffeaa7; color: #856404;';
            case 'regular':
                return 'background-color: #d4edda; color: #155724;';
            case 'postseason':
                return 'background-color: #f8d7da; color: #721c24;';
            default:
                return '';
        }
    }}
`;

const MatchesList = styled.div`
    margin-top: 0.5em;
`;

const MatchItem = styled.div`
    padding: 0.4em;
    margin: 0.15em 0;
    background-color: white;
    border-radius: 4px;
    border: 1px solid #e9ecef;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const MatchInfo = styled.div`
    flex: 1;
`;

const MatchStatus = styled.span<{ $hasResults: boolean }>`
    padding: 0.25em 0.5em;
    border-radius: 4px;
    font-size: 0.8em;
    font-weight: 500;
    margin-left: 0.5em;
    
    ${props => props.$hasResults ? 
        'background-color: #d4edda; color: #155724;' : 
        'background-color: #fff3cd; color: #856404;'
    }
`;

const OvertimeIndicator = styled.span`
    color: #dc3545;
    font-weight: 600;
    margin-left: 0.5em;
`;

const MatchActions = styled.div`
    display: flex;
    gap: 0.25em;
`;

const MatchActionButton = styled.button`
    background-color: #dc3545;
    color: white;
    border: none;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8em;
    
    &:hover {
        background-color: #c82333;
    }
`;

const EditResultsButton = styled.button`
    background-color: #17a2b8;
    color: white;
    border: none;
    padding: 0.25em 0.5em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.8em;
    
    &:hover {
        background-color: #138496;
    }
`;

const AddWeekSection = styled.div`
    margin-top: 0.75em;
    padding: 0.75em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f8f9fa;
`;

const AddWeekForm = styled.div`
    display: flex;
    gap: 0.5em;
    align-items: center;
    flex-wrap: wrap;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2em;
    min-width: 150px;
`;

const Label = styled.label`
    font-size: 0.9em;
    color: #666;
    font-weight: 500;
`;

const NumberInput = styled.input`
    padding: 0.5em;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 80px;
`;

const BulkAddSection = styled.div`
    margin-top: 0.75em;
    padding: 0.75em;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f8f9fa;
`;

const BulkAddForm = styled.div`
    display: flex;
    gap: 0.75em;
    align-items: end;
    flex-wrap: wrap;
`;

const BulkAddButton = styled.button`
    background-color: #17a2b8;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
        background-color: #138496;
    }
    
    &:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
`;

const AddMatchSection = styled.div`
    margin-top: 0.75em;
    padding: 0.75em;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    background-color: white;
`;

const AddMatchForm = styled.div`
    display: flex;
    gap: 0.4em;
    align-items: center;
    flex-wrap: wrap;
`;

const MatchFormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.2em;
    min-width: 200px;
`;

const MatchSelect = styled.select`
    padding: 0.5em;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: white;
`;

const MatchButton = styled.button`
    background-color: #17a2b8;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9em;
    
    &:hover {
        background-color: #138496;
    }
    
    &:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
`;

const CancelButton = styled.button`
    background-color: #6c757d;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
        background-color: #545b62;
    }
`;

const SaveButton = styled.button`
    background-color: #28a745;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    margin-left: 0.5em;
    
    &:hover {
        background-color: #218838;
    }
    
    &:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
`;

const EditButton = styled.button`
    background-color: #007bff;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
        background-color: #0056b3;
    }
`;

const ResultsForm = styled.div`
    margin-top: 0.75em;
    padding: 0.75em;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    background-color: white;
`;

const ResultsFormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 0.75em;
    align-items: center;
    margin-bottom: 0.75em;
`;

const TeamColumn = styled.div`
    text-align: center;
`;

const TeamName = styled.div`
    font-weight: 600;
    margin-bottom: 0.5em;
    color: #333;
`;

const ScoreInput = styled.input`
    padding: 0.5em;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 80px;
    text-align: center;
    font-size: 1.2em;
    font-weight: 600;
`;

const CasInput = styled.input`
    padding: 0.5em;
    border: 1px solid #ccc;
    border-radius: 4px;
    width: 60px;
    text-align: center;
`;

const VersusText = styled.div`
    font-size: 1.2em;
    font-weight: 600;
    color: #666;
    text-align: center;
`;

const OvertimeSection = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5em;
    margin-top: 0.75em;
    padding-top: 0.75em;
    border-top: 1px solid #e9ecef;
`;

const OvertimeCheckbox = styled.input`
    margin: 0;
`;

const OvertimeLabel = styled.label`
    font-weight: 500;
    color: #666;
`;

const ResultsActions = styled.div`
    display: flex;
    gap: 0.5em;
    margin-top: 0.75em;
`;

const ResultsButton = styled.button`
    background-color: #28a745;
    color: white;
    border: none;
    padding: 0.5em 1em;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9em;
    
    &:hover {
        background-color: #218838;
    }
    
    &:disabled {
        background-color: #6c757d;
        cursor: not-allowed;
    }
`;

const ResultsDisplay = styled.div`
    margin-top: 0.5em;
    padding: 0.5em;
    background-color: #f8f9fa;
    border-radius: 4px;
    font-size: 0.9em;
`;

const ResultsText = styled.div`
    margin-bottom: 0.25em;
`;

interface SeasonConfigurationProps {
    seasonData: {
        divisions: DivisionModel[];
        weeks: WeekModel[];
        playoffStructure?: PlayoffStructure;
    };
    leagueCoaches: CoachModel[];
    editMode: boolean;
    onEditModeChange?: (editMode: boolean) => void;
    onSeasonDataChange?: (newData: { divisions: DivisionModel[]; weeks: WeekModel[]; playoffStructure?: PlayoffStructure }) => void;
    onSave?: () => void;
    onCancel?: () => void;
}

export const SeasonConfiguration: React.FC<SeasonConfigurationProps> = ({
    seasonData,
    leagueCoaches,
    editMode,
    onEditModeChange,
    onSeasonDataChange,
    onSave,
    onCancel
}) => {
    // Validate and normalize data structure
    const safeSeasonData = React.useMemo(() => {
        if (!seasonData) {
            return { divisions: [], weeks: [], playoffStructure: undefined };
        }
        const ps = seasonData.playoffStructure;
        const validPlayoff = ps && typeof ps.divisionWinnersPerDivision === 'number' && typeof ps.wildcardCount === 'number' && typeof ps.byeCount === 'number'
            ? ps
            : undefined;
        return {
            divisions: Array.isArray(seasonData.divisions) ? seasonData.divisions : [],
            weeks: Array.isArray(seasonData.weeks) ? seasonData.weeks : [],
            playoffStructure: validPlayoff
        };
    }, [seasonData]);

    const safeLeagueCoaches = React.useMemo(() => {
        return Array.isArray(leagueCoaches) ? leagueCoaches : [];
    }, [leagueCoaches]);

    // Validate data structure
    if (!seasonData) {
        const logger = createComponentLogger('SeasonConfiguration', 'validateData');
        logger.error('Invalid season data structure', { seasonData });
        return (
            <div>
                <h3>Season Configuration</h3>
                <div data-testid="error">Error: Invalid season data structure</div>
            </div>
        );
    }
    const [newDivisionName, setNewDivisionName] = useState('');
    const [selectedCoachId, setSelectedCoachId] = useState<string>('');
    const [newTeamName, setNewTeamName] = useState('');
    const [newTeamRace, setNewTeamRace] = useState('Human');
    const [divisionsExpanded, setDivisionsExpanded] = useState(true);
    const [weeksExpanded, setWeeksExpanded] = useState(false);
    const [playoffsExpanded, setPlayoffsExpanded] = useState(false);
    const [showAddWeekForm, setShowAddWeekForm] = useState(false);
    const [newWeekName, setNewWeekName] = useState('');
    const [preseasonWeeks, setPreseasonWeeks] = useState(0);
    const [regularWeeks, setRegularWeeks] = useState(8);
    const [postseasonWeeks, setPostseasonWeeks] = useState(2);
    const [showAddMatchForm, setShowAddMatchForm] = useState<number | null>(null);
    const [selectedHomeTeam, setSelectedHomeTeam] = useState<string>('');
    const [selectedAwayTeam, setSelectedAwayTeam] = useState<string>('');
    const [showResultsForm, setShowResultsForm] = useState<{ weekIndex: number; matchIndex: number } | null>(null);
    const [resultsFormData, setResultsFormData] = useState({
        homeTeamScore: 0,
        awayTeamScore: 0,
        homeTeamCasInflicted: 0,
        awayTeamCasInflicted: 0,
        overtime: false
    });

    // Get all available teams for match creation
    const getAvailableTeams = () => {
        const allTeams: TeamModel[] = [];
        if (!safeSeasonData || !Array.isArray(safeSeasonData.divisions)) return allTeams;
        safeSeasonData.divisions.forEach(division => {
            if (!division || !Array.isArray(division.teams)) return;
            division.teams.forEach(team => {
                if (team) {
                    allTeams.push(team);
                }
            });
        });
        return allTeams;
    };

    // Get available teams for a specific week (excluding those already in matches)
    const getAvailableTeamsForWeek = (weekIndex: number) => {
        const allTeams = getAvailableTeams();
        if (!safeSeasonData || !Array.isArray(safeSeasonData.weeks) || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
            return allTeams;
        }
        const week = safeSeasonData.weeks[weekIndex];
        if (!week || !Array.isArray(week.matches)) return allTeams;
        
        // Get all teams already in matches for this week
        const teamsInMatches = new Set<string>();
        week.matches.forEach(match => {
            if (match) {
                if (match.homeTeamId) teamsInMatches.add(match.homeTeamId);
                if (match.awayTeamId) teamsInMatches.add(match.awayTeamId);
            }
        });
        
        // Filter out teams already in matches
        return allTeams.filter(team => team && team.teamId && !teamsInMatches.has(team.teamId));
    };

    // Get all coaches that are not assigned to any division
    const getUnassignedCoaches = () => {
        const assignedCoachIds = new Set<string>();
        if (!safeSeasonData || !Array.isArray(safeSeasonData.divisions)) {
            return safeLeagueCoaches;
        }
        safeSeasonData.divisions.forEach(division => {
            if (!division || !Array.isArray(division.teams)) return;
            division.teams.forEach(team => {
                if (team && team.coachId) {
                    assignedCoachIds.add(team.coachId);
                }
            });
        });
        
        return safeLeagueCoaches.filter(coach => coach && coach.coachId && !assignedCoachIds.has(coach.coachId));
    };

    const handleAddDivision = () => {
        if (!newDivisionName.trim()) return;

        const newDivision: DivisionModel = {
            divisionName: newDivisionName.trim(),
            teams: []
        };

        const updatedSeasonData = {
            ...safeSeasonData,
            divisions: [...safeSeasonData.divisions, newDivision]
        };

        onSeasonDataChange?.(updatedSeasonData);
        setNewDivisionName('');
    };

    const handleRemoveDivision = (divisionIndex: number) => {
        if (!safeSeasonData || !Array.isArray(safeSeasonData.divisions) || divisionIndex < 0 || divisionIndex >= safeSeasonData.divisions.length) {
            return;
        }
        const updatedDivisions = safeSeasonData.divisions.filter((_, index) => index !== divisionIndex);
        const updatedSeasonData = {
            ...safeSeasonData,
            divisions: updatedDivisions
        };

        onSeasonDataChange?.(updatedSeasonData);
    };

    const handleAddCoachToDivision = (divisionIndex: number, coachId: string) => {
        const coach = safeLeagueCoaches.find(c => c && c.coachId === coachId);
        if (!coach || !newTeamName.trim()) return;

        if (!safeSeasonData || !Array.isArray(safeSeasonData.divisions) || divisionIndex < 0 || divisionIndex >= safeSeasonData.divisions.length) {
            return;
        }

        // Create a new team for this coach
        const newTeam: TeamModel = {
            teamId: `team-${coach.coachId}`,
            teamName: newTeamName.trim(),
            coachId: coach.coachId,
            race: newTeamRace
        };

        const updatedDivisions = [...safeSeasonData.divisions];
        if (!updatedDivisions[divisionIndex] || !Array.isArray(updatedDivisions[divisionIndex].teams)) {
            return;
        }
        const existingTeam = updatedDivisions[divisionIndex].teams.find(team => team && team.coachId === coach.coachId);
        if (!existingTeam) {
            updatedDivisions[divisionIndex].teams.push(newTeam);
        }

        const updatedSeasonData = {
            ...safeSeasonData,
            divisions: updatedDivisions
        };

        onSeasonDataChange?.(updatedSeasonData);
        setSelectedCoachId('');
        setNewTeamName('');
        setNewTeamRace('Human');
    };

    const handleRemoveCoachFromDivision = (divisionIndex: number, teamId: string) => {
        if (!safeSeasonData || !Array.isArray(safeSeasonData.divisions) || divisionIndex < 0 || divisionIndex >= safeSeasonData.divisions.length) {
            return;
        }
        const updatedDivisions = [...safeSeasonData.divisions];
        if (!updatedDivisions[divisionIndex] || !Array.isArray(updatedDivisions[divisionIndex].teams)) {
            return;
        }
        updatedDivisions[divisionIndex].teams = updatedDivisions[divisionIndex].teams.filter(team => team && team.teamId !== teamId);

        const updatedSeasonData = {
            ...safeSeasonData,
            divisions: updatedDivisions
        };

        onSeasonDataChange?.(updatedSeasonData);
    };

    // Week management handlers
    const handleAddWeek = () => {
        if (!newWeekName.trim()) return;

        const newWeek: WeekModel = {
            weekName: newWeekName.trim(),
            matches: []
        };

        const updatedSeasonData = {
            ...safeSeasonData,
            weeks: [...safeSeasonData.weeks, newWeek]
        };

        onSeasonDataChange?.(updatedSeasonData);
        setNewWeekName('');
        setShowAddWeekForm(false);
    };

    const handleRemoveWeek = (weekIndex: number) => {
        if (!safeSeasonData || !Array.isArray(safeSeasonData.weeks) || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
            return;
        }
        const updatedWeeks = safeSeasonData.weeks.filter((_, index) => index !== weekIndex);
        const updatedSeasonData = {
            ...safeSeasonData,
            weeks: updatedWeeks
        };

        onSeasonDataChange?.(updatedSeasonData);
    };

    const handleBulkAddWeeks = () => {
        const newWeeks: WeekModel[] = [];
        let preseasonCounter = 1;
        let regularCounter = 1;
        let postseasonCounter = 1;

        // Add preseason weeks
        for (let i = 0; i < preseasonWeeks; i++) {
            newWeeks.push({
                weekName: `Preseason Week ${preseasonCounter}`,
                matches: []
            });
            preseasonCounter++;
        }

        // Add regular season weeks (always start at Week 1)
        for (let i = 0; i < regularWeeks; i++) {
            newWeeks.push({
                weekName: `Week ${regularCounter}`,
                matches: []
            });
            regularCounter++;
        }

        // Add postseason weeks (always start at Postseason Week 1)
        for (let i = 0; i < postseasonWeeks; i++) {
            newWeeks.push({
                weekName: `Postseason Week ${postseasonCounter}`,
                matches: []
            });
            postseasonCounter++;
        }

        const updatedSeasonData = {
            ...safeSeasonData,
            weeks: [...safeSeasonData.weeks, ...newWeeks]
        };

        onSeasonDataChange?.(updatedSeasonData);
    };

    // Match management handlers
    const handleAddMatch = (weekIndex: number) => {
        if (!selectedHomeTeam || !selectedAwayTeam || selectedHomeTeam === selectedAwayTeam) return;
        if (!safeSeasonData || !Array.isArray(safeSeasonData.weeks) || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
            return;
        }

        const newMatch = {
            homeTeamId: selectedHomeTeam,
            awayTeamId: selectedAwayTeam
        };

        const updatedWeeks = [...safeSeasonData.weeks];
        if (!updatedWeeks[weekIndex] || !Array.isArray(updatedWeeks[weekIndex].matches)) {
            return;
        }
        updatedWeeks[weekIndex].matches.push(newMatch);

        const updatedSeasonData = {
            ...safeSeasonData,
            weeks: updatedWeeks
        };

        onSeasonDataChange?.(updatedSeasonData);
        setSelectedHomeTeam('');
        setSelectedAwayTeam('');
        setShowAddMatchForm(null);
    };

    const handleRemoveMatch = (weekIndex: number, matchIndex: number) => {
        if (!safeSeasonData || !Array.isArray(safeSeasonData.weeks) || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
            return;
        }
        const updatedWeeks = [...safeSeasonData.weeks];
        if (!updatedWeeks[weekIndex] || !Array.isArray(updatedWeeks[weekIndex].matches) || matchIndex < 0 || matchIndex >= updatedWeeks[weekIndex].matches.length) {
            return;
        }
        updatedWeeks[weekIndex].matches.splice(matchIndex, 1);

        const updatedSeasonData = {
            ...safeSeasonData,
            weeks: updatedWeeks
        };

        onSeasonDataChange?.(updatedSeasonData);
    };

    // Results management handlers
    const handleShowResultsForm = (weekIndex: number, matchIndex: number) => {
        if (!safeSeasonData || !Array.isArray(safeSeasonData.weeks) || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
            return;
        }
        const week = safeSeasonData.weeks[weekIndex];
        if (!week || !Array.isArray(week.matches) || matchIndex < 0 || matchIndex >= week.matches.length) {
            return;
        }
        const match = week.matches[matchIndex];
        if (!match) return;
        
        if (match.results) {
            setResultsFormData({
                homeTeamScore: match.results.homeTeamScore,
                awayTeamScore: match.results.awayTeamScore,
                homeTeamCasInflicted: match.results.homeTeamCasInflicted,
                awayTeamCasInflicted: match.results.awayTeamCasInflicted,
                overtime: match.results.overtime
            });
        } else {
            setResultsFormData({
                homeTeamScore: 0,
                awayTeamScore: 0,
                homeTeamCasInflicted: 0,
                awayTeamCasInflicted: 0,
                overtime: false
            });
        }
        setShowResultsForm({ weekIndex, matchIndex });
    };

    const handleSaveResults = (weekIndex: number, matchIndex: number) => {
        if (!safeSeasonData || !Array.isArray(safeSeasonData.weeks) || weekIndex < 0 || weekIndex >= safeSeasonData.weeks.length) {
            return;
        }
        const updatedWeeks = [...safeSeasonData.weeks];
        if (!updatedWeeks[weekIndex] || !Array.isArray(updatedWeeks[weekIndex].matches) || matchIndex < 0 || matchIndex >= updatedWeeks[weekIndex].matches.length) {
            return;
        }
        updatedWeeks[weekIndex] = {
            ...updatedWeeks[weekIndex],
            matches: [...updatedWeeks[weekIndex].matches]
        };
        updatedWeeks[weekIndex].matches[matchIndex] = {
            ...updatedWeeks[weekIndex].matches[matchIndex],
            results: {
                homeTeamScore: resultsFormData.homeTeamScore,
                awayTeamScore: resultsFormData.awayTeamScore,
                homeTeamCasInflicted: resultsFormData.homeTeamCasInflicted,
                awayTeamCasInflicted: resultsFormData.awayTeamCasInflicted,
                overtime: resultsFormData.overtime
            }
        };

        const updatedSeasonData = {
            ...safeSeasonData,
            weeks: updatedWeeks
        };

        onSeasonDataChange?.(updatedSeasonData);
        setShowResultsForm(null);
    };

    const handleCancelResults = () => {
        setShowResultsForm(null);
        setResultsFormData({
            homeTeamScore: 0,
            awayTeamScore: 0,
            homeTeamCasInflicted: 0,
            awayTeamCasInflicted: 0,
            overtime: false
        });
    };

    const unassignedCoaches = getUnassignedCoaches();

    return (
        <>
            {editMode && (
                <EditModeBanner>
                    <span>Edit Mode - Make your changes and click "Save Changes" when done</span>
                </EditModeBanner>
            )}

            <div>
                {!editMode && onEditModeChange && (
                    <EditButton onClick={() => onEditModeChange(true)}>
                        Edit Season Structure
                    </EditButton>
                )}
                {editMode && onEditModeChange && (
                    <div style={{ display: 'flex', gap: '0.5em', marginBottom: '1em' }}>
                        <CancelButton onClick={onCancel}>
                            Cancel
                        </CancelButton>
                        <SaveButton onClick={onSave}>
                            Save Changes
                        </SaveButton>
                    </div>
                )}
            </div>

            <CollapsibleSection>
                <SectionHeader 
                    $isExpanded={divisionsExpanded}
                    onClick={() => setDivisionsExpanded(!divisionsExpanded)}
                >
                    <SectionTitle>Divisions ({safeSeasonData.divisions.length})</SectionTitle>
                    <ToggleButton>
                        {divisionsExpanded ? '−' : '+'}
                    </ToggleButton>
                </SectionHeader>
                <SectionContent $isExpanded={divisionsExpanded}>
                    {safeSeasonData.divisions.length > 0 ? (
                        <DivisionsList>
                            {safeSeasonData.divisions.map((division, divisionIndex) => (
                                <DivisionItem key={divisionIndex}>
                                    <DivisionHeader>
                                        <DivisionName>{division.divisionName}</DivisionName>
                                        {editMode && (
                                            <DivisionActions>
                                                <ActionButton 
                                                    className="delete"
                                                    onClick={() => handleRemoveDivision(divisionIndex)}
                                                >
                                                    Remove Division
                                                </ActionButton>
                                            </DivisionActions>
                                        )}
                                    </DivisionHeader>
                                    
                                    <TeamsList>
                                        {division.teams && division.teams.length > 0 ? (
                                            division.teams.map((team) => {
                                                if (!team) return null;
                                                const coach = Array.isArray(leagueCoaches) ? leagueCoaches.find(c => c && c.coachId === team.coachId) : null;
                                                return (
                                                    <TeamItem key={team.teamId}>
                                                        <CoachInfo>
                                                            <strong>{team.teamName}</strong> ({team.race}) - {coach ? coach.coachName : `Coach ${team.coachId}`}
                                                        </CoachInfo>
                                                        {editMode && (
                                                            <RemoveButton 
                                                                onClick={() => handleRemoveCoachFromDivision(divisionIndex, team.teamId)}
                                                            >
                                                                Remove
                                                            </RemoveButton>
                                                        )}
                                                    </TeamItem>
                                                );
                                            })
                                        ) : (
                                            <p>No teams in this division.</p>
                                        )}
                                    </TeamsList>

                                    {editMode && (
                                        <AddCoachSection>
                                            <h4>Add Team to Division</h4>
                                            <AddCoachForm>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5em' }}>
                                                    <Select 
                                                        value={selectedCoachId} 
                                                        onChange={(e) => setSelectedCoachId(e.target.value)}
                                                        disabled={unassignedCoaches.length === 0}
                                                    >
                                                        <option value="">Select a coach...</option>
                                                        {unassignedCoaches.map((coach) => (
                                                            <option key={coach.coachId} value={coach.coachId}>
                                                                {coach.coachName}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                    <Input 
                                                        type="text"
                                                        value={newTeamName}
                                                        onChange={(e) => setNewTeamName(e.target.value)}
                                                        placeholder="Enter team name"
                                                    />
                                                    <Select 
                                                        value={newTeamRace} 
                                                        onChange={(e) => setNewTeamRace(e.target.value)}
                                                    >
                                                        <option value="Human">Human</option>
                                                        <option value="Orc">Orc</option>
                                                        <option value="Skaven">Skaven</option>
                                                        <option value="Wood Elves">Wood Elves</option>
                                                        <option value="Dark Elves">Dark Elves</option>
                                                        <option value="Chaos">Chaos</option>
                                                        <option value="Undead">Undead</option>
                                                        <option value="Elven Union">Elven Union</option>
                                                        <option value="Old World Alliance">Old World Alliance</option>
                                                    </Select>
                                                    <AddButton 
                                                        onClick={() => handleAddCoachToDivision(divisionIndex, selectedCoachId)}
                                                        disabled={!selectedCoachId || !newTeamName.trim() || unassignedCoaches.length === 0}
                                                    >
                                                        Add Team
                                                    </AddButton>
                                                </div>
                                            </AddCoachForm>
                                            {unassignedCoaches.length === 0 && (
                                                <p>All available coaches are already assigned to divisions.</p>
                                            )}
                                        </AddCoachSection>
                                    )}
                                </DivisionItem>
                            ))}
                        </DivisionsList>
                    ) : (
                        <p>No divisions configured for this season.</p>
                    )}
                    
                    {editMode && (
                        <AddDivisionSection>
                            <h3>Add Division</h3>
                            <AddDivisionForm>
                                <Input 
                                    type="text"
                                    value={newDivisionName}
                                    onChange={(e) => setNewDivisionName(e.target.value)}
                                    placeholder="Enter division name"
                                    onKeyPress={(e) => e.key === 'Enter' && handleAddDivision()}
                                />
                                <AddButton 
                                    onClick={handleAddDivision}
                                    disabled={!newDivisionName.trim()}
                                >
                                    Add Division
                                </AddButton>
                            </AddDivisionForm>
                        </AddDivisionSection>
                    )}
                </SectionContent>
            </CollapsibleSection>

            <CollapsibleSection data-testid="weeks-section">
                <SectionHeader 
                    $isExpanded={weeksExpanded}
                    onClick={() => setWeeksExpanded(!weeksExpanded)}
                >
                    <SectionTitle>Weeks ({safeSeasonData.weeks.length})</SectionTitle>
                    <ToggleButton>
                        {weeksExpanded ? '−' : '+'}
                    </ToggleButton>
                </SectionHeader>
                <SectionContent $isExpanded={weeksExpanded}>
                    {safeSeasonData.weeks.length > 0 ? (
                        <WeeksList>
                            {safeSeasonData.weeks.map((week, weekIndex) => {
                                const weekType = week.weekName.toLowerCase().includes('preseason') ? 'preseason' :
                                               week.weekName.toLowerCase().includes('postseason') ? 'postseason' : 'regular';
                                
                                return (
                                    <WeekItem key={weekIndex}>
                                        <WeekHeader>
                                            <WeekName>
                                                {week.weekName}
                                                <WeekTypeBadge type={weekType}>
                                                    {weekType}
                                                </WeekTypeBadge>
                                            </WeekName>
                                            {editMode && (
                                                <WeekActions>
                                                    <ActionButton 
                                                        className="delete"
                                                        onClick={() => handleRemoveWeek(weekIndex)}
                                                    >
                                                        Remove Week
                                                    </ActionButton>
                                                </WeekActions>
                                            )}
                                        </WeekHeader>
                                        
                                        <MatchesList>
                                            {week.matches && week.matches.length > 0 ? (
                                                week.matches.map((match, matchIndex) => {
                                                    if (!match) return null;
                                                    const homeCoach = Array.isArray(leagueCoaches) ? leagueCoaches.find(c => c && c.coachId === match.homeTeamId) : null;
                                                    const awayCoach = Array.isArray(leagueCoaches) ? leagueCoaches.find(c => c && c.coachId === match.awayTeamId) : null;
                                                    const hasResults = match.results !== undefined;
                                                    const isEditingResults = showResultsForm?.weekIndex === weekIndex && showResultsForm?.matchIndex === matchIndex;
                                                    
                                                    return (
                                                        <div key={matchIndex}>
                                                            <MatchItem>
                                                                <MatchInfo>
                                                                    {awayCoach ? awayCoach.coachName : `Coach ${match.awayTeamId}`} @ {homeCoach ? homeCoach.coachName : `Coach ${match.homeTeamId}`}
                                                                    <MatchStatus $hasResults={hasResults}>
                                                                        {hasResults ? 'Results Entered' : 'No Results'}
                                                                    </MatchStatus>
                                                                    {hasResults && match.results?.overtime && (
                                                                        <OvertimeIndicator>OT</OvertimeIndicator>
                                                                    )}
                                                                    {hasResults && !isEditingResults && match.results && (
                                                                        <ResultsDisplay>
                                                                            <ResultsText>
                                                                                <strong>Score:</strong> {match.results.awayTeamScore} - {match.results.homeTeamScore}
                                                                                {match.results.overtime && <OvertimeIndicator>OT</OvertimeIndicator>}
                                                                            </ResultsText>
                                                                            <ResultsText>
                                                                                <strong>Casualties:</strong> {match.results.awayTeamCasInflicted} - {match.results.homeTeamCasInflicted}
                                                                            </ResultsText>
                                                                        </ResultsDisplay>
                                                                    )}
                                                                </MatchInfo>
                                                                <MatchActions>
                                                                    <EditResultsButton 
                                                                        onClick={() => handleShowResultsForm(weekIndex, matchIndex)}
                                                                    >
                                                                        {hasResults ? 'Edit Results' : 'Enter Results'}
                                                                    </EditResultsButton>
                                                                    {editMode && (
                                                                        <MatchActionButton 
                                                                            onClick={() => handleRemoveMatch(weekIndex, matchIndex)}
                                                                        >
                                                                            Remove
                                                                        </MatchActionButton>
                                                                    )}
                                                                </MatchActions>
                                                            </MatchItem>
                                                            
                                                            {isEditingResults && (
                                                                <ResultsForm>
                                                                    <h4>Enter Match Results</h4>
                                                                    <ResultsFormGrid>
                                                                        <TeamColumn>
                                                                            <TeamName>{awayCoach ? awayCoach.coachName : `Coach ${match.awayTeamId}`}</TeamName>
                                                                            <div>
                                                                                <Label>Score:</Label>
                                                                                <ScoreInput
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max="10"
                                                                                    value={resultsFormData.awayTeamScore}
                                                                                    onChange={(e) => setResultsFormData({
                                                                                        ...resultsFormData,
                                                                                        awayTeamScore: parseInt(e.target.value) || 0
                                                                                    })}
                                                                                />
                                                                            </div>
                                                                            <div style={{ marginTop: '0.5em' }}>
                                                                                <Label>Casualties:</Label>
                                                                                <CasInput
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max="10"
                                                                                    value={resultsFormData.awayTeamCasInflicted}
                                                                                    onChange={(e) => setResultsFormData({
                                                                                        ...resultsFormData,
                                                                                        awayTeamCasInflicted: parseInt(e.target.value) || 0
                                                                                    })}
                                                                                />
                                                                            </div>
                                                                        </TeamColumn>
                                                                        <VersusText>@</VersusText>
                                                                        <TeamColumn>
                                                                            <TeamName>{homeCoach ? homeCoach.coachName : `Coach ${match.homeTeamId}`}</TeamName>
                                                                            <div>
                                                                                <Label>Score:</Label>
                                                                                <ScoreInput
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max="10"
                                                                                    value={resultsFormData.homeTeamScore}
                                                                                    onChange={(e) => setResultsFormData({
                                                                                        ...resultsFormData,
                                                                                        homeTeamScore: parseInt(e.target.value) || 0
                                                                                    })}
                                                                                />
                                                                            </div>
                                                                            <div style={{ marginTop: '0.5em' }}>
                                                                                <Label>Casualties:</Label>
                                                                                <CasInput
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max="10"
                                                                                    value={resultsFormData.homeTeamCasInflicted}
                                                                                    onChange={(e) => setResultsFormData({
                                                                                        ...resultsFormData,
                                                                                        homeTeamCasInflicted: parseInt(e.target.value) || 0
                                                                                    })}
                                                                                />
                                                                            </div>
                                                                        </TeamColumn>
                                                                    </ResultsFormGrid>
                                                                    
                                                                    <OvertimeSection>
                                                                        <OvertimeCheckbox
                                                                            type="checkbox"
                                                                            id={`overtime-${weekIndex}-${matchIndex}`}
                                                                            checked={resultsFormData.overtime}
                                                                            onChange={(e) => setResultsFormData({
                                                                                ...resultsFormData,
                                                                                overtime: e.target.checked
                                                                            })}
                                                                        />
                                                                        <OvertimeLabel htmlFor={`overtime-${weekIndex}-${matchIndex}`}>
                                                                            Overtime Game
                                                                        </OvertimeLabel>
                                                                    </OvertimeSection>
                                                                    
                                                                    <ResultsActions>
                                                                        <ResultsButton 
                                                                            onClick={() => handleSaveResults(weekIndex, matchIndex)}
                                                                        >
                                                                            Save Results
                                                                        </ResultsButton>
                                                                        <CancelButton onClick={handleCancelResults}>
                                                                            Cancel
                                                                        </CancelButton>
                                                                    </ResultsActions>
                                                                </ResultsForm>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <p>No matches scheduled for this week.</p>
                                            )}
                                        </MatchesList>

                                        {editMode && (
                                            <AddMatchSection>
                                                {showAddMatchForm === weekIndex ? (
                                                    <div>
                                                        <h4>Add Match to {week.weekName}</h4>
                                                        <AddMatchForm>
                                                            <MatchFormGroup>
                                                                <Label>Away Team:</Label>
                                                                <MatchSelect 
                                                                    value={selectedAwayTeam} 
                                                                    onChange={(e) => setSelectedAwayTeam(e.target.value)}
                                                                    disabled={getAvailableTeamsForWeek(weekIndex).length < 2}
                                                                >
                                                                    <option value="">Select away team...</option>
                                                                    {getAvailableTeamsForWeek(weekIndex).map((team) => {
                                                                        if (!team) return null;
                                                                        const coach = Array.isArray(leagueCoaches) ? leagueCoaches.find(c => c && c.coachId === team.coachId) : null;
                                                                        return (
                                                                            <option key={team.teamId} value={team.teamId}>
                                                                                {team.teamName} ({team.race}) - {coach ? coach.coachName : `Coach ${team.coachId || 'unknown'}`}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </MatchSelect>
                                                            </MatchFormGroup>
                                                            <MatchFormGroup>
                                                                <Label>Home Team:</Label>
                                                                <MatchSelect 
                                                                    value={selectedHomeTeam} 
                                                                    onChange={(e) => setSelectedHomeTeam(e.target.value)}
                                                                    disabled={getAvailableTeamsForWeek(weekIndex).length < 2}
                                                                >
                                                                    <option value="">Select home team...</option>
                                                                    {getAvailableTeamsForWeek(weekIndex).map((team) => {
                                                                        if (!team) return null;
                                                                        const coach = Array.isArray(leagueCoaches) ? leagueCoaches.find(c => c && c.coachId === team.coachId) : null;
                                                                        return (
                                                                            <option key={team.teamId} value={team.teamId}>
                                                                                {team.teamName} ({team.race}) - {coach ? coach.coachName : `Coach ${team.coachId}`}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </MatchSelect>
                                                            </MatchFormGroup>
                                                            <MatchButton 
                                                                onClick={() => handleAddMatch(weekIndex)}
                                                                disabled={!selectedHomeTeam || !selectedAwayTeam || selectedHomeTeam === selectedAwayTeam || getAvailableTeamsForWeek(weekIndex).length < 2}
                                                            >
                                                                Add Match
                                                            </MatchButton>
                                                            <CancelButton onClick={() => {
                                                                setShowAddMatchForm(null);
                                                                setSelectedHomeTeam('');
                                                                setSelectedAwayTeam('');
                                                            }}>
                                                                Cancel
                                                            </CancelButton>
                                                        </AddMatchForm>
                                                        {getAvailableTeamsForWeek(weekIndex).length < 2 && (
                                                            <p style={{ color: '#dc3545', fontSize: '0.9em', marginTop: '0.5em' }}>
                                                                Need at least 2 teams available for this week to create matches.
                                                            </p>
                                                        )}
                                                    </div>
                                                ) : (
                                                                                                            <div>
                                                            <MatchButton onClick={() => setShowAddMatchForm(weekIndex)}>
                                                                Add Match +
                                                            </MatchButton>
                                                            {getAvailableTeamsForWeek(weekIndex).length < 2 && (
                                                                <p style={{ color: '#dc3545', fontSize: '0.9em', marginTop: '0.5em' }}>
                                                                    Need at least 2 teams available for this week to create matches.
                                                                </p>
                                                            )}
                                                        </div>
                                                )}
                                            </AddMatchSection>
                                        )}
                                    </WeekItem>
                                );
                            })}
                        </WeeksList>
                    ) : (
                        <p>No weeks configured for this season.</p>
                    )}
                    
                    {editMode && (
                        <>
                            <BulkAddSection>
                                <h3>Bulk Add Weeks</h3>
                                <BulkAddForm>
                                    <FormGroup>
                                        <Label>Preseason Weeks:</Label>
                                        <NumberInput
                                            type="number"
                                            min="0"
                                            max="10"
                                            value={preseasonWeeks}
                                            onChange={(e) => setPreseasonWeeks(parseInt(e.target.value) || 0)}
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Regular Season Weeks:</Label>
                                        <NumberInput
                                            type="number"
                                            min="1"
                                            max="20"
                                            value={regularWeeks}
                                            onChange={(e) => setRegularWeeks(parseInt(e.target.value) || 1)}
                                        />
                                    </FormGroup>
                                    <FormGroup>
                                        <Label>Postseason Weeks:</Label>
                                        <NumberInput
                                            type="number"
                                            min="0"
                                            max="8"
                                            value={postseasonWeeks}
                                            onChange={(e) => setPostseasonWeeks(parseInt(e.target.value) || 0)}
                                        />
                                    </FormGroup>
                                    <BulkAddButton 
                                        onClick={handleBulkAddWeeks}
                                        disabled={preseasonWeeks + regularWeeks + postseasonWeeks === 0}
                                    >
                                        Add {preseasonWeeks + regularWeeks + postseasonWeeks} Weeks
                                    </BulkAddButton>
                                </BulkAddForm>
                            </BulkAddSection>

                            <AddWeekSection>
                                <h3>Add Single Week</h3>
                                {!showAddWeekForm ? (
                                    <AddButton onClick={() => setShowAddWeekForm(true)}>
                                        Add Week +
                                    </AddButton>
                                ) : (
                                    <AddWeekForm>
                                        <Input 
                                            type="text"
                                            value={newWeekName}
                                            onChange={(e) => setNewWeekName(e.target.value)}
                                            placeholder="Enter week name (e.g., 'Week 1', 'Preseason Week 1')"
                                            onKeyPress={(e) => e.key === 'Enter' && handleAddWeek()}
                                        />
                                        <AddButton 
                                            onClick={handleAddWeek}
                                            disabled={!newWeekName.trim()}
                                        >
                                            Add Week
                                        </AddButton>
                                        <CancelButton onClick={() => setShowAddWeekForm(false)}>
                                            Cancel
                                        </CancelButton>
                                    </AddWeekForm>
                                )}
                            </AddWeekSection>
                        </>
                    )}
                </SectionContent>
            </CollapsibleSection>

            <CollapsibleSection data-testid="playoff-structure-section">
                <SectionHeader
                    $isExpanded={playoffsExpanded}
                    onClick={() => setPlayoffsExpanded(!playoffsExpanded)}
                >
                    <SectionTitle>Playoff structure</SectionTitle>
                    <ToggleButton>
                        {playoffsExpanded ? '−' : '+'}
                    </ToggleButton>
                </SectionHeader>
                <SectionContent $isExpanded={playoffsExpanded}>
                    <p style={{ marginTop: 0, color: '#555' }}>
                        Division winners use division tiebreakers (incl. division points). Wildcards use league-wide tiebreakers when comparing teams from different divisions; when two non-winners are in the same division, division points apply like division standings.
                        Seeds 1–N are division winners ordered that way, then wildcards. Top seeds get a bye. Single elimination, lowest vs highest.
                    </p>
                    <BulkAddForm>
                        <FormGroup>
                            <Label>Division winners per division:</Label>
                            <NumberInput
                                type="number"
                                min="1"
                                max="4"
                                value={safeSeasonData.playoffStructure?.divisionWinnersPerDivision ?? 1}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (!Number.isNaN(v) && v >= 1) {
                                        onSeasonDataChange?.({
                                            ...safeSeasonData,
                                            playoffStructure: {
                                                divisionWinnersPerDivision: v,
                                                wildcardCount: safeSeasonData.playoffStructure?.wildcardCount ?? 2,
                                                byeCount: safeSeasonData.playoffStructure?.byeCount ?? 2,
                                                seeding: 'divisionWinnersFirst',
                                                bracket: 'singleElimination',
                                                bracketMatchup: 'lowestVsHighest'
                                            }
                                        });
                                    }
                                }}
                                disabled={!editMode}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Wildcard count:</Label>
                            <NumberInput
                                type="number"
                                min="0"
                                max="8"
                                value={safeSeasonData.playoffStructure?.wildcardCount ?? 2}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (!Number.isNaN(v) && v >= 0) {
                                        onSeasonDataChange?.({
                                            ...safeSeasonData,
                                            playoffStructure: {
                                                divisionWinnersPerDivision: safeSeasonData.playoffStructure?.divisionWinnersPerDivision ?? 1,
                                                wildcardCount: v,
                                                byeCount: safeSeasonData.playoffStructure?.byeCount ?? 2,
                                                seeding: 'divisionWinnersFirst',
                                                bracket: 'singleElimination',
                                                bracketMatchup: 'lowestVsHighest'
                                            }
                                        });
                                    }
                                }}
                                disabled={!editMode}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Byes (top seeds with first-round bye):</Label>
                            <NumberInput
                                type="number"
                                min="0"
                                max="8"
                                value={safeSeasonData.playoffStructure?.byeCount ?? 2}
                                onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (!Number.isNaN(v) && v >= 0) {
                                        onSeasonDataChange?.({
                                            ...safeSeasonData,
                                            playoffStructure: {
                                                divisionWinnersPerDivision: safeSeasonData.playoffStructure?.divisionWinnersPerDivision ?? 1,
                                                wildcardCount: safeSeasonData.playoffStructure?.wildcardCount ?? 2,
                                                byeCount: v,
                                                seeding: 'divisionWinnersFirst',
                                                bracket: 'singleElimination',
                                                bracketMatchup: 'lowestVsHighest'
                                            }
                                        });
                                    }
                                }}
                                disabled={!editMode}
                            />
                        </FormGroup>
                    </BulkAddForm>
                </SectionContent>
            </CollapsibleSection>
        </>
    );
}; 